/**
 * Pagination Component
 *
 * Componente de paginação reutilizável seguindo padrão shadcn/ui.
 * Usa cn() + buttonVariants para estilização consistente com o design system.
 * Suporta compound components, dark mode via CSS variables e variantes.
 *
 * @module components/ui
 * @since 2025-12-16
 * @updated 2026-04-06 - Migrado para shadcn/ui (cn() + buttonVariants + React.forwardRef)
 * @epic INIT-0046 Epic 0002
 *
 * @example
 * // Paginação numerada (padrão)
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 * />
 *
 * @example
 * // Paginação simples com texto
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 *   variant="simple"
 * />
 *
 * @example
 * // Paginação compacta (ideal para mobile)
 * <Pagination
 *   currentPage={page}
 *   totalPages={totalPages}
 *   onPageChange={setPage}
 *   variant="compact"
 *   size="sm"
 * />
 *
 * @example
 * // Paginação completa com info e seletor de tamanho
 * <div className="flex items-center justify-between">
 *   <PaginationInfo
 *     currentPage={page}
 *     pageSize={pageSize}
 *     totalItems={total}
 *   />
 *   <Pagination
 *     currentPage={page}
 *     totalPages={Math.ceil(total / pageSize)}
 *     onPageChange={setPage}
 *     showFirstLast
 *   />
 *   <PageSizeSelector
 *     pageSize={pageSize}
 *     onPageSizeChange={setPageSize}
 *   />
 * </div>
 *
 * @example
 * // Compound components (shadcn pattern)
 * <PaginationRoot>
 *   <PaginationContent>
 *     <PaginationItem>
 *       <PaginationPrevious onClick={() => setPage(p => p - 1)} />
 *     </PaginationItem>
 *     <PaginationItem>
 *       <PaginationLink isActive onClick={() => setPage(1)}>1</PaginationLink>
 *     </PaginationItem>
 *     <PaginationItem>
 *       <PaginationEllipsis />
 *     </PaginationItem>
 *     <PaginationItem>
 *       <PaginationNext onClick={() => setPage(p => p + 1)} />
 *     </PaginationItem>
 *   </PaginationContent>
 * </PaginationRoot>
 */

import * as React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, MoreHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/Button';

// ==========================================
// TYPES
// ==========================================

/** Variantes visuais da paginação */
type PaginationVariant = 'simple' | 'numbered' | 'compact';

/** Tamanhos da paginação */
type PaginationSize = 'sm' | 'md' | 'lg';

/**
 * Props do componente Pagination
 */
interface PaginationProps {
  /** Página atual (1-indexed) */
  currentPage: number;
  /** Total de páginas */
  totalPages: number;
  /** Callback chamado quando a página muda */
  onPageChange: (page: number) => void;
  /** Variante visual: simple (texto), numbered (botões), compact (mínimo) @default 'numbered' */
  variant?: PaginationVariant;
  /** Tamanho dos botões @default 'md' */
  size?: PaginationSize;
  /** Se true, exibe botões de primeira/última página @default false */
  showFirstLast?: boolean;
  /** Se true, exibe botões anterior/próximo @default true */
  showPrevNext?: boolean;
  /** Quantidade de páginas visíveis de cada lado da atual @default 1 */
  siblingCount?: number;
  /** Classes CSS adicionais */
  className?: string;
  /** Se true, desabilita toda a paginação @default false */
  disabled?: boolean;
  /** Tamanho da página (para compatibilidade com API) */
  pageSize?: number;
  /** Callback para mudança de tamanho de página (para compatibilidade com API) */
  onPageSizeChange?: (size: number) => void;
}

/**
 * Props do componente PaginationInfo
 */
interface PaginationInfoProps {
  /** Página atual */
  currentPage: number;
  /** Itens por página */
  pageSize: number;
  /** Total de itens */
  totalItems: number;
  /** Classes CSS adicionais */
  className?: string;
}

// ==========================================
// SIZE MAP
// ==========================================

const sizeMap: Record<PaginationSize, { btnSize: 'sm' | 'md'; iconClass: string }> = {
  sm: { btnSize: 'sm', iconClass: 'h-4 w-4' },
  md: { btnSize: 'md', iconClass: 'h-4 w-4' },
  lg: { btnSize: 'md', iconClass: 'h-5 w-5' },
};

// ==========================================
// HELPER: Generate page numbers
// ==========================================

function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  siblingCount: number
): (number | 'ellipsis')[] {
  const totalNumbers = siblingCount * 2 + 3; // siblings + current + first + last
  const totalBlocks = totalNumbers + 2; // + 2 ellipsis

  if (totalPages <= totalBlocks) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

  const showLeftEllipsis = leftSiblingIndex > 2;
  const showRightEllipsis = rightSiblingIndex < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const leftItemCount = 3 + 2 * siblingCount;
    const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
    return [...leftRange, 'ellipsis', totalPages];
  }

  if (showLeftEllipsis && !showRightEllipsis) {
    const rightItemCount = 3 + 2 * siblingCount;
    const rightRange = Array.from(
      { length: rightItemCount },
      (_, i) => totalPages - rightItemCount + i + 1
    );
    return [1, 'ellipsis', ...rightRange];
  }

  if (showLeftEllipsis && showRightEllipsis) {
    const middleRange = Array.from(
      { length: rightSiblingIndex - leftSiblingIndex + 1 },
      (_, i) => leftSiblingIndex + i
    );
    return [1, 'ellipsis', ...middleRange, 'ellipsis', totalPages];
  }

  return Array.from({ length: totalPages }, (_, i) => i + 1);
}

// ==========================================
// COMPOUND COMPONENTS (shadcn/ui pattern)
// ==========================================

/**
 * Root nav wrapper — equivalente ao <nav> do shadcn Pagination
 */
const PaginationRoot = React.forwardRef<
  HTMLElement,
  React.ComponentProps<'nav'>
>(({ className, ...props }, ref) => (
  <nav
    ref={ref}
    role="navigation"
    aria-label="pagination"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
));
PaginationRoot.displayName = 'PaginationRoot';

/**
 * Lista de itens de paginação
 */
const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-1', className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

/**
 * Item individual da lista
 */
const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

/**
 * Link/botão de página — usa buttonVariants para estilização
 */
interface PaginationLinkProps extends React.ComponentProps<'button'> {
  isActive?: boolean;
  size?: 'sm' | 'md';
}

const PaginationLink = React.forwardRef<HTMLButtonElement, PaginationLinkProps>(
  ({ className, isActive, size = 'sm', ...props }, ref) => (
    <button
      ref={ref}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        buttonVariants({
          variant: isActive ? 'primary' : 'outline',
          size,
        }),
        'min-w-[2rem] px-2',
        className
      )}
      {...props}
    />
  )
);
PaginationLink.displayName = 'PaginationLink';

/**
 * Botão "Anterior"
 */
interface PaginationPreviousProps extends React.ComponentProps<'button'> {
  iconClass?: string;
  label?: string;
}

const PaginationPrevious = React.forwardRef<HTMLButtonElement, PaginationPreviousProps>(
  ({ className, iconClass = 'h-4 w-4', label, ...props }, ref) => {
    const { t } = useTranslation(['buttons', 'common']);
    return (
      <button
        ref={ref}
        aria-label={label ?? t('Previous page', { ns: 'common' })}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'gap-1',
          className
        )}
        {...props}
      >
        <ChevronLeft className={iconClass} />
      </button>
    );
  }
);
PaginationPrevious.displayName = 'PaginationPrevious';

/**
 * Botão "Próximo"
 */
interface PaginationNextProps extends React.ComponentProps<'button'> {
  iconClass?: string;
  label?: string;
}

const PaginationNext = React.forwardRef<HTMLButtonElement, PaginationNextProps>(
  ({ className, iconClass = 'h-4 w-4', label, ...props }, ref) => {
    const { t } = useTranslation(['buttons', 'common']);
    return (
      <button
        ref={ref}
        aria-label={label ?? t('Next page', { ns: 'common' })}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'gap-1',
          className
        )}
        {...props}
      >
        <ChevronRight className={iconClass} />
      </button>
    );
  }
);
PaginationNext.displayName = 'PaginationNext';

/**
 * Ellipsis (...)
 */
const PaginationEllipsis = React.forwardRef<
  HTMLSpanElement,
  React.ComponentProps<'span'>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden
    className={cn('flex h-9 w-9 items-center justify-center text-muted-foreground', className)}
    {...props}
  >
    <MoreHorizontal className="h-4 w-4" />
    <span className="sr-only">More pages</span>
  </span>
));
PaginationEllipsis.displayName = 'PaginationEllipsis';

// ==========================================
// MAIN COMPONENT
// ==========================================

export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  variant = 'numbered',
  size = 'md',
  showFirstLast = false,
  showPrevNext = true,
  siblingCount = 1,
  className,
  disabled = false,
}: PaginationProps) {
  const { t } = useTranslation(['buttons', 'common']);
  const pageNumbers = React.useMemo(
    () => generatePageNumbers(currentPage, totalPages, siblingCount),
    [currentPage, totalPages, siblingCount]
  );

  if (totalPages <= 1) return null;

  const canGoPrev = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const { btnSize, iconClass } = sizeMap[size];

  // Simple variant
  if (variant === 'simple') {
    return (
      <div className={cn('flex items-center justify-between', className)}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={disabled || !canGoPrev}
          className={cn(
            'inline-flex items-center gap-1 font-medium transition-colors',
            'text-muted-foreground hover:text-foreground',
            (disabled || !canGoPrev) && 'opacity-50 cursor-not-allowed'
          )}
        >
          <ChevronLeft className={iconClass} />
          {t('buttons:previous')}
        </button>
        <span className="text-sm text-muted-foreground">
          {t('Page', { ns: 'common' })} {currentPage} {t('of', { ns: 'common' })} {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={disabled || !canGoNext}
          className={cn(
            'inline-flex items-center gap-1 font-medium transition-colors',
            'text-muted-foreground hover:text-foreground',
            (disabled || !canGoNext) && 'opacity-50 cursor-not-allowed'
          )}
        >
          {t('buttons:next')}
          <ChevronRight className={iconClass} />
        </button>
      </div>
    );
  }

  // Compact variant
  if (variant === 'compact') {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={disabled || !canGoPrev}
          aria-label={t('Previous page', { ns: 'common' })}
          className={cn(
            buttonVariants({ variant: 'outline', size: btnSize }),
            disabled || !canGoPrev ? 'opacity-50 cursor-not-allowed' : ''
          )}
        >
          <ChevronLeft className={iconClass} />
        </button>
        <span className="px-2 text-sm text-muted-foreground">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={disabled || !canGoNext}
          aria-label={t('Next page', { ns: 'common' })}
          className={cn(
            buttonVariants({ variant: 'outline', size: btnSize }),
            disabled || !canGoNext ? 'opacity-50 cursor-not-allowed' : ''
          )}
        >
          <ChevronRight className={iconClass} />
        </button>
      </div>
    );
  }

  // Numbered variant (default) — usando compound components
  return (
    <PaginationRoot className={className} aria-label={t('Pagination', { ns: 'common' })}>
      <PaginationContent>
        {/* First */}
        {showFirstLast && (
          <PaginationItem>
            <button
              onClick={() => onPageChange(1)}
              disabled={disabled || !canGoPrev}
              aria-label={t('First page', { ns: 'common' })}
              className={cn(
                buttonVariants({ variant: 'outline', size: btnSize }),
                (disabled || !canGoPrev) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronsLeft className={iconClass} />
            </button>
          </PaginationItem>
        )}

        {/* Previous */}
        {showPrevNext && (
          <PaginationItem>
            <PaginationPrevious
              onClick={() => onPageChange(currentPage - 1)}
              disabled={disabled || !canGoPrev}
              iconClass={iconClass}
              label={t('Previous page', { ns: 'common' })}
              className={cn((disabled || !canGoPrev) && 'opacity-50 cursor-not-allowed')}
            />
          </PaginationItem>
        )}

        {/* Page numbers */}
        {pageNumbers.map((page, index) => {
          if (page === 'ellipsis') {
            return (
              <PaginationItem key={`ellipsis-${index}`}>
                <PaginationEllipsis />
              </PaginationItem>
            );
          }

          return (
            <PaginationItem key={page}>
              <PaginationLink
                isActive={page === currentPage}
                onClick={() => onPageChange(page)}
                disabled={disabled}
                size={btnSize}
                aria-label={`${t('Page', { ns: 'common' })} ${page}`}
              >
                {page}
              </PaginationLink>
            </PaginationItem>
          );
        })}

        {/* Next */}
        {showPrevNext && (
          <PaginationItem>
            <PaginationNext
              onClick={() => onPageChange(currentPage + 1)}
              disabled={disabled || !canGoNext}
              iconClass={iconClass}
              label={t('Next page', { ns: 'common' })}
              className={cn((disabled || !canGoNext) && 'opacity-50 cursor-not-allowed')}
            />
          </PaginationItem>
        )}

        {/* Last */}
        {showFirstLast && (
          <PaginationItem>
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={disabled || !canGoNext}
              aria-label={t('Last page', { ns: 'common' })}
              className={cn(
                buttonVariants({ variant: 'outline', size: btnSize }),
                (disabled || !canGoNext) && 'opacity-50 cursor-not-allowed'
              )}
            >
              <ChevronsRight className={iconClass} />
            </button>
          </PaginationItem>
        )}
      </PaginationContent>
    </PaginationRoot>
  );
}

// ==========================================
// PAGINATION INFO
// ==========================================

export function PaginationInfo({
  currentPage,
  pageSize,
  totalItems,
  className,
}: PaginationInfoProps) {
  const { t } = useTranslation('common');
  const start = (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalItems);

  return (
    <p className={cn('text-sm text-muted-foreground', className)}>
      {t('Showing')} <span className="font-medium">{start}</span> {t('to')}{' '}
      <span className="font-medium">{end}</span> {t('of')}{' '}
      <span className="font-medium">{totalItems}</span> {t('Results').toLowerCase()}
    </p>
  );
}

// ==========================================
// PAGE SIZE SELECTOR
// ==========================================

interface PageSizeSelectorProps {
  pageSize: number;
  onPageSizeChange: (size: number) => void;
  options?: number[];
  className?: string;
}

export function PageSizeSelector({
  pageSize,
  onPageSizeChange,
  options = [10, 25, 50, 100],
  className,
}: PageSizeSelectorProps) {
  const { t } = useTranslation('common');
  return (
    <div className={cn('flex items-center gap-2 text-sm', className)}>
      <label htmlFor="page-size" className="text-muted-foreground">
        {t('Items per page')}:
      </label>
      <select
        id="page-size"
        value={pageSize}
        onChange={(e) => onPageSizeChange(Number(e.target.value))}
        className={cn(
          'rounded-lg border border-border bg-card px-2 py-1 text-sm',
          'text-foreground',
          'focus:outline-none focus:ring-2 focus:ring-offset-1',
          'focus:ring-[var(--brand-primary)]'
        )}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </div>
  );
}

// ==========================================
// COMPOUND EXPORTS (shadcn/ui pattern)
// ==========================================

export {
  PaginationRoot,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
